// TEN8-F1-S3 mounting and lifecycle boundaries, independent of other settings grants.
import {
  locationPermitRequest,
  type LocationPermitContextDto,
  type LocationPermitInput,
} from "./api.ts";
import { createLocationPermits, type LocationPermitView } from "./locationPermits.ts";
import type { ConsoleHost } from "./softwareOwner.ts";
import { hasPermission, type Session } from "./session.ts";
export function initPlatformPermitPanels(host: Pick<ConsoleHost, "request">) {
  let epoch = 0;
  let views: LocationPermitView[] = [];
  let failures: HTMLElement[] = [];
  const clear = () => {
    epoch++;
    views.forEach((view) => view.clear());
    views = [];
    failures.forEach((failure) => failure.remove());
    failures = [];
  };
  async function load(
    tenantId: string,
    targets: Array<{ id: string; container: HTMLElement }>,
    active: () => boolean,
  ): Promise<void> {
    clear();
    const captured = epoch,
      base = `/platform/organizations/${encodeURIComponent(tenantId)}`;
    const current = () => captured === epoch && active();
    let response;
    try {
      response = await host.request<LocationPermitContextDto>(
        "GET",
        `${base}/location-permit-context`,
      );
    } catch {
      response = { status: 0, body: null };
    }
    if (!current()) return;
    if (
      response.status !== 200 ||
      !Array.isArray(response.body?.locations) ||
      typeof response.body.canManage !== "boolean"
    ) {
      for (const target of targets) {
        const error = document.createElement("p");
        error.textContent = "Could not load permits.";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "btn-secondary";
        retry.textContent = "Retry";
        retry.onclick = () => {
          if (current()) {
            error.remove();
            retry.remove();
            void load(tenantId, targets, active);
          }
        };
        const failure = document.createElement("div");
        failure.append(error, retry);
        target.container.append(failure);
        failures.push(failure);
      }
      return;
    }
    const context = response.body;
    for (const target of targets) {
      if (!context.locations.some((choice) => choice.id === target.id)) continue;
      const view = createLocationPermits(target.container, {
        request: (method, path, body) => host.request(method, path, body),
        basePath: `${base}/locations/${encodeURIComponent(target.id)}/permits`,
        locationId: target.id,
        active: current,
        canManage: () => context.canManage,
      });
      views.push(view);
      void view.load();
    }
  }
  window.addEventListener("pagehide", clear);
  return {
    load,
    clear,
    clearSensitive: clear,
    dirty: () => views.some((view) => view.dirty()),
    busy: () => views.some((view) => view.busy()),
    confirmLeave: () =>
      !views.some((view) => view.busy()) &&
      (!views.some((view) => view.dirty()) ||
        window.confirm("Discard the unsaved permit changes?")),
  };
}
export function initTenantPermitPanel(
  parent: HTMLElement,
  currentSession: () => Session | undefined,
) {
  const container = document.createElement("section");
  container.className = "admin-subsection";
  container.hidden = true;
  const label = document.createElement("label");
  label.className = "field-label";
  label.textContent = "Location";
  const choices = document.createElement("select");
  choices.className = "field-input";
  choices.id = `location-permit-picker-${crypto.randomUUID()}`;
  label.htmlFor = choices.id;
  const message = document.createElement("p");
  message.setAttribute("role", "status");
  const retry = document.createElement("button");
  retry.type = "button";
  retry.className = "btn-secondary";
  retry.textContent = "Retry";
  retry.hidden = true;
  const body = document.createElement("div");
  container.append(label, choices, message, retry, body);
  parent.append(container);
  let epoch = 0,
    view: LocationPermitView | undefined,
    activeSession: Session | undefined,
    token: string | undefined,
    context: LocationPermitContextDto | undefined;
  function reset(): void {
    epoch++;
    view?.clear();
    view = undefined;
    activeSession = undefined;
    token = undefined;
    context = undefined;
    choices.replaceChildren();
    body.replaceChildren();
    message.textContent = "";
    container.hidden = true;
  }
  const active = (captured: number): boolean => {
    const latest = currentSession();
    return (
      captured === epoch &&
      activeSession !== undefined &&
      latest !== undefined &&
      latest.token === token &&
      latest.tenantId === activeSession.tenantId &&
      latest.userId === activeSession.userId &&
      hasPermission(latest, "admin.location_permits.view")
    );
  };
  function mount(): void {
    view?.clear();
    view = undefined;
    body.replaceChildren();
    const captured = epoch,
      session = activeSession,
      locationId = choices.value;
    if (session === undefined || !locationId || !active(captured)) return;
    view = createLocationPermits(body, {
      request: <T>(method: "GET" | "POST", path: string, bodyInput?: LocationPermitInput) =>
        locationPermitRequest<T>(session.apiBaseUrl, session.tenantId, method, path, bodyInput),
      basePath: `/locations/${encodeURIComponent(locationId)}/permits`,
      locationId,
      active: () => active(captured) && choices.value === locationId,
      canManage: () =>
        context?.canManage === true &&
        currentSession() !== undefined &&
        hasPermission(currentSession()!, "admin.location_permits.manage"),
    });
    void view.load();
  }
  async function load(session: Session, preferredLocationId?: string): Promise<void> {
    if (
      activeSession?.tenantId === session.tenantId &&
      activeSession.userId === session.userId &&
      token === session.token &&
      context !== undefined &&
      !container.hidden
    )
      return;
    reset();
    if (!hasPermission(session, "admin.location_permits.view")) return;
    activeSession = session;
    token = session.token;
    const captured = epoch;
    container.hidden = false;
    choices.disabled = true;
    message.textContent = "Loading permits.";
    retry.hidden = true;
    try {
      const response = await locationPermitRequest<LocationPermitContextDto>(
        session.apiBaseUrl,
        session.tenantId,
        "GET",
        "/location-permit-context",
      );
      if (!active(captured)) return;
      if (
        response.status !== 200 ||
        !Array.isArray(response.body?.locations) ||
        typeof response.body.canManage !== "boolean"
      ) {
        message.textContent =
          response.status === 401 || response.status === 403
            ? "Permits are no longer available."
            : "Could not load permits.";
        retry.hidden = false;
        return;
      }
      context = response.body;
      choices.replaceChildren();
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = "Select location";
      choices.append(blank);
      for (const locationChoice of context.locations) {
        const option = document.createElement("option");
        option.value = locationChoice.id;
        option.textContent = locationChoice.name;
        choices.append(option);
      }
      choices.value = context.locations.some((choice) => choice.id === preferredLocationId)
        ? preferredLocationId!
        : context.locations.length === 1
          ? context.locations[0]!.id
          : "";
      choices.disabled = false;
      message.textContent = context.locations.length ? "" : "No locations";
      mount();
    } catch {
      if (active(captured)) {
        message.textContent = "Could not load permits.";
        retry.hidden = false;
      }
    }
  }
  let previousLocation = "";
  choices.addEventListener("focus", () => {
    previousLocation = choices.value;
  });
  choices.addEventListener("change", () => {
    if (view && !view.confirmLeave()) {
      choices.value = previousLocation;
      return;
    }
    previousLocation = choices.value;
    mount();
  });
  retry.onclick = () => {
    const session = currentSession();
    if (session) void load(session, choices.value);
  };
  window.addEventListener("pagehide", reset);
  window.addEventListener("beforeunload", (event) => {
    if (view?.dirty() || view?.busy()) event.preventDefault();
  });
  return {
    load,
    reset,
    dirty: () => view?.dirty() ?? false,
    busy: () => view?.busy() ?? false,
    refreshAccess: () => {
      if (activeSession === undefined) return;
      if (!active(epoch) || !hasPermission(activeSession, "admin.location_permits.view")) {
        reset();
        return;
      }
      view?.refreshAccess();
    },
    confirmLeave: () => view?.confirmLeave() ?? true,
  };
}
